import Header from '../components/Header'
import Footer from '../components/Footer'
import { 
  ArrowRight, Shield, Zap, Users, CheckCircle, 
  CreditCard, Smartphone, QrCode, ShoppingBag,
  FileText, Lock, TrendingUp, Phone, Mail,
  Wallet
} from 'lucide-react'
import { Link } from 'react-router-dom'

const HomePage = () => {
  const whyUs = [
    {
      icon: <CheckCircle className="h-8 w-8 text-orange-500" />,
      title: "Self Sign-Up",
      description: "Paper-less and contact-free! Our digital sign-up portal lets you seamlessly register from the comfort of your office or home."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-orange-500" />,
      title: "Optimize Revenue",
      description: "Integrate with ease and simplicity to expedite acceptance of payments and grow your business revenue."
    },
    {
      icon: <Shield className="h-8 w-8 text-orange-500" />,
      title: "Security",
      description: "We have a Fraud Monitoring Solution and transaction monitoring team in place to protect your business from fraud and unauthorized transactions."
    },
    {
      icon: <Users className="h-8 w-8 text-orange-500" />,
      title: "24/7 Support",
      description: "There to assist you every step of the way — 24/7 customer support through email, chat, and call, plus an extensive FAQ page."
    }
  ]

  const products = [
    {
      icon: <CreditCard className="h-10 w-10 text-orange-500" />,
      title: "Payment Gateway",
      description: "A secure checkout page for e-commerce retailers. Accept payments via cards, mobile wallets, and bank accounts.",
      link: "/products"
    },
    {
      icon: <FileText className="h-10 w-10 text-orange-500" />,
      title: "Bill Payments",
      description: "Billing made hassle-free. Create and issue invoices and collect payments through any bank's mobile or internet banking.",
      link: "/products"
    },
    {
      icon: <QrCode className="h-10 w-10 text-orange-500" />,
      title: "Mobile Payments",
      description: "Enabling QR and Tap & Pay transactions throughout Pakistan via our UnionPay International partnership.",
      link: "/products"
    },
    {
      icon: <ShoppingBag className="h-10 w-10 text-orange-500" />,
      title: "E-Commerce Plugins",
      description: "Pro-developer plugins for the most popular e-commerce platforms. Powerful, adaptable, and customizable integration.",
      link: "/products"
    }
  ]

  const paymentMethods = [
    { label: "Visa" },
    { label: "Mastercard" },
    { label: "UnionPay" },
    { label: "JazzCash" },
    { label: "EasyPaisa" },
    { label: "NayaPay" },
    { label: "SadaPay" },
    { label: "Bank Transfer" },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        {/* decorative blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="container-max section-padding relative py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              {/* badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-400 text-sm font-medium">
                <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                Built for businesses of every size
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Welcome to the{' '}
                <span className="text-orange-500">New Era</span>
                <br />of Digital Payments!
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
                MobiPay is a cutting-edge fintech that allows any business to receive payments
                from their customers through a variety of online payment methods.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/get-started"
                  className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-orange-500/30 hover:shadow-xl"
                >
                  Ready to Get Started?
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center border border-gray-600 hover:border-orange-500 text-gray-300 hover:text-orange-400 px-8 py-4 rounded-lg font-semibold transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>

              {/* payment method pills */}
              <div className="flex flex-wrap gap-2 pt-2">
                {paymentMethods.map((m) => (
                  <span key={m.label} className="px-3 py-1 bg-white/10 border border-white/10 rounded-full text-xs text-gray-300 font-medium">
                    {m.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Dashboard card */}
            <div className="relative">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                <div className="bg-orange-500 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-bold text-lg">MobiPay Dashboard</h3>
                    <p className="text-orange-100 text-sm">Real-time payment overview</p>
                  </div>
                  <div className="bg-white/20 rounded-lg px-3 py-1 text-white text-xs font-medium">Live</div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <p className="text-gray-400 text-xs mb-1">Total Revenue</p>
                      <p className="text-white text-2xl font-bold">₨ 2.4M</p>
                      <p className="text-green-400 text-xs mt-1">↑ 15.3% this month</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <p className="text-gray-400 text-xs mb-1">Transactions</p>
                      <p className="text-white text-2xl font-bold">1,247</p>
                      <p className="text-green-400 text-xs mt-1">↑ 12.8% this month</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      <span>Success Rate</span>
                      <span className="text-green-400 font-semibold">99.2%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="bg-orange-500 h-2 rounded-full" style={{ width: '99.2%' }} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-gray-400 text-xs uppercase tracking-wide">Recent Transactions</p>
                    {[
                      { label: "Online Payment • Card", amount: "₨ 15,000" },
                      { label: "JazzCash Wallet", amount: "₨ 8,500" },
                      { label: "Bank Transfer", amount: "₨ 25,000" },
                    ].map((tx, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                        <span className="text-gray-300 text-sm">{tx.label}</span>
                        <span className="text-green-400 text-sm font-semibold">{tx.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* floating badges */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg">
                Visa Partner
              </div>
              <div className="absolute -bottom-4 -left-4 bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg">
                Built for growth
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Time to go cashless banner ── */}
      <section className="bg-orange-500 text-white py-16">
        <div className="container-max section-padding text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Time to go cashless!</h2>
          <p className="text-orange-100 text-lg max-w-3xl mx-auto mb-8">
            We're here to reduce dependency on cash. MobiPay aims to ensure a robust digital payment platform
            that is secure, reliable, and revolutionizes the way you operate your business.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-lg font-bold transition-all duration-200 shadow-md"
          >
            Ready to Get Started?
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* ── Why MobiPay ── */}
      <section className="py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
              Why MobiPay?
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why MobiPay?
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              MobiPay makes it easier to run your business as Pakistan moves towards digitization.
              We're here to help scale your business by enabling it to go online and reach untapped markets.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-50 rounded-2xl mb-6">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform overview ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-orange-500 text-white rounded-xl font-bold text-lg shadow-lg">
                <Shield className="h-6 w-6" />
                Payments made simple
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                One platform for the entire payment journey
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                Give customers a smooth way to pay and give your team a clearer way to manage every
                transaction. From checkout to reconciliation, MobiPay keeps the essentials in one place.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium">
                  <CheckCircle className="h-4 w-4" /> Flexible checkout
                </div>
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium">
                  <CheckCircle className="h-4 w-4" /> Real-time insights
                </div>
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium">
                  <CheckCircle className="h-4 w-4" /> Fraud Monitoring
                </div>
              </div>
              <Link to="/products" className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors">
                Explore payment solutions <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
                <Lock className="h-10 w-10 text-orange-500 mx-auto mb-4" />
                <h4 className="font-bold text-lg mb-2">Fraud Protection</h4>
                <p className="text-gray-400 text-sm">Real-time monitoring and automated fraud prevention</p>
              </div>
              <div className="bg-orange-500 text-white rounded-2xl p-8 text-center">
                <Shield className="h-10 w-10 text-white mx-auto mb-4" />
                <h4 className="font-bold text-lg mb-2">256-bit Encryption</h4>
                <p className="text-orange-100 text-sm">Bank-grade SSL encryption for every transaction</p>
              </div>
              <div className="bg-orange-50 rounded-2xl p-8 text-center">
                <Zap className="h-10 w-10 text-orange-500 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 text-lg mb-2">Instant Settlements</h4>
                <p className="text-gray-500 text-sm">Fast, reliable settlement directly to your account</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8 text-center border border-gray-200">
                <TrendingUp className="h-10 w-10 text-orange-500 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 text-lg mb-2">99.9% Uptime</h4>
                <p className="text-gray-500 text-sm">Reliable infrastructure with guaranteed availability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Products ── */}
      <section className="py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
              Our Solutions
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Everything your business needs
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              A dynamic platform to accept digital payments through payment gateway, payment links, billing, and more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map((p, i) => (
              <Link
                key={i}
                to={p.link}
                className="group bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-orange-50 group-hover:bg-orange-500 rounded-2xl flex items-center justify-center transition-colors duration-300">
                    <div className="text-orange-500 group-hover:text-white transition-colors duration-300">
                      {p.icon}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed">{p.description}</p>
                  <div className="flex items-center mt-4 text-orange-500 font-medium text-sm">
                    Learn more <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bill Payments ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: illustration-style card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-gray-400 text-sm">Bill Payments Portal</p>
                  <p className="text-2xl font-bold mt-1">Hassle-free Billing</p>
                </div>
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-white" />
                </div>
              </div>
              {[
                { label: "Invoice #1023", status: "Paid", amount: "₨ 45,000", color: "text-green-400" },
                { label: "Invoice #1024", status: "Pending", amount: "₨ 12,000", color: "text-yellow-400" },
                { label: "Invoice #1025", status: "Paid", amount: "₨ 78,500", color: "text-green-400" },
              ].map((inv, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-white/10 last:border-0">
                  <div>
                    <p className="text-white font-medium text-sm">{inv.label}</p>
                    <p className={`text-xs ${inv.color}`}>{inv.status}</p>
                  </div>
                  <p className="text-white font-bold">{inv.amount}</p>
                </div>
              ))}
              <div className="mt-6 pt-6 border-t border-white/10 flex gap-3">
                <div className="flex-1 bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Mobile Banking</p>
                  <p className="text-white font-bold text-sm mt-1">✓ Supported</p>
                </div>
                <div className="flex-1 bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Internet Banking</p>
                  <p className="text-white font-bold text-sm mt-1">✓ Supported</p>
                </div>
                <div className="flex-1 bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Over-the-Counter</p>
                  <p className="text-white font-bold text-sm mt-1">✓ Supported</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                Bill Payments
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Allow us to take care of Bill Payments
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                Billing made hassle-free. MobiPay Billing is made for companies of all sizes to create
                and issue invoices and collect payments and fees through any bank's mobile banking,
                internet banking, and over-the-counter locations.
              </p>
              <ul className="space-y-3">
                {[
                  "Create professional branded invoices in seconds",
                  "Accept payments through all major banks",
                  "Mobile banking, internet banking & OTC",
                  "Automated payment reminders",
                  "Real-time payment tracking and reporting",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/products" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                Explore Bill Payments <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mobile Payments / QR ── */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 rounded-full text-sm font-medium">
                Mobile Payments
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold">
                Enabling QR and Tap &amp; Pay transactions throughout Pakistan
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                We've partnered with UnionPay International, the world's largest payment scheme,
                in order to facilitate mobile payments. Accept QR code payments and contactless
                Tap &amp; Pay transactions at your business.
              </p>
              <ul className="space-y-3">
                {[
                  "QR code payments — scan and pay instantly",
                  "Tap & Pay with NFC-enabled devices",
                  "UnionPay International partnership",
                  "Works across all major mobile wallets",
                  "In-store and online mobile payments",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/products" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-orange-500/30">
                Explore Mobile Payments <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="bg-gray-800 rounded-3xl p-8 border border-gray-700">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-3xl mb-4">
                  <QrCode className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold">Scan to Pay</h3>
                <p className="text-gray-400 text-sm mt-1">Point your camera at a QR code</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Smartphone className="h-6 w-6 text-orange-400" />, label: "JazzCash", sub: "Mobile Wallet" },
                  { icon: <Smartphone className="h-6 w-6 text-orange-400" />, label: "EasyPaisa", sub: "Mobile Wallet" },
                  { icon: <Wallet className="h-6 w-6 text-orange-400" />, label: "NayaPay", sub: "Digital Bank" },
                  { icon: <Wallet className="h-6 w-6 text-orange-400" />, label: "SadaPay", sub: "Digital Bank" },
                ].map((w, i) => (
                  <div key={i} className="bg-gray-700/50 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                      {w.icon}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{w.label}</p>
                      <p className="text-gray-400 text-xs">{w.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                <p className="text-orange-400 font-semibold text-sm">Powered by UnionPay International</p>
                <p className="text-gray-400 text-xs mt-1">World's largest payment scheme</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── E-commerce Plugins ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "WooCommerce", color: "bg-purple-50 border-purple-200", text: "text-purple-700", desc: "WordPress plugin" },
                  { name: "Shopify", color: "bg-green-50 border-green-200", text: "text-green-700", desc: "Shopify app" },
                  { name: "Magento", color: "bg-orange-50 border-orange-200", text: "text-orange-700", desc: "Magento extension" },
                  { name: "OpenCart", color: "bg-blue-50 border-blue-200", text: "text-blue-700", desc: "OpenCart module" },
                  { name: "REST API", color: "bg-gray-50 border-gray-200", text: "text-gray-700", desc: "Custom integration" },
                  { name: "SDK", color: "bg-red-50 border-red-200", text: "text-red-700", desc: "iOS & Android" },
                ].map((p, i) => (
                  <div key={i} className={`${p.color} border rounded-xl p-5 text-center hover:shadow-md transition-shadow`}>
                    <ShoppingBag className={`h-7 w-7 ${p.text} mx-auto mb-2`} />
                    <p className={`font-bold ${p.text}`}>{p.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                E-Commerce Integration
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                We're Pro-Developers on the most popular E-commerce plugins
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                Delivering powerful, adaptable, and customizable payments integration. Whether you're
                on WooCommerce, Shopify, or building a custom solution, we have you covered.
              </p>
              <ul className="space-y-3">
                {[
                  "One-click plugin installation",
                  "Sandbox testing environment",
                  "Comprehensive developer documentation",
                  "Dedicated developer support",
                  "Webhook support for real-time events",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/products" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                View All Integrations <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Security is our highest priority ── */}
      <section className="py-24 bg-gray-50">
        <div className="container-max section-padding text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
            <Lock className="h-4 w-4" /> Enterprise Security
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
            Security is our highest priority
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-16">
            Every transaction is protected by multiple layers of security, fraud monitoring,
            and regulatory compliance — so you and your customers can transact with confidence.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Shield className="h-10 w-10 text-orange-500" />,
                title: "PCI-DSS Level 1",
                description: "The highest level of PCI-DSS compliance — the global standard for payment security."
              },
              {
                icon: <Lock className="h-10 w-10 text-orange-500" />,
                title: "Fraud Monitoring",
                description: "Advanced Guardian fraud monitoring solution with a dedicated team watching every transaction 24/7."
              },
              {
                icon: <Zap className="h-10 w-10 text-orange-500" />,
                title: "3D Secure",
                description: "Additional authentication layer for online card payments to protect against unauthorised use."
              },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-50 rounded-2xl mb-6">
                  {s.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-500 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>

          <Link to="/security" className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 border border-orange-200 px-6 py-3 rounded-lg hover:bg-orange-50 transition-all">
            Read more about our security <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="max-w-2xl mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
              A simpler way to get paid
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">From sign-up to your first payment</h2>
            <p className="text-gray-500 text-lg leading-relaxed">
              Build a payment experience that fits your business without adding unnecessary complexity to your team or customers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Create your account', description: 'Tell us about your business and choose the payment tools that match how you sell.' },
              { step: '02', title: 'Connect your checkout', description: 'Use a plugin, payment link, QR code, or API to add payments to your existing flow.' },
              { step: '03', title: 'Track every payment', description: 'Follow transactions, invoices, and performance from one easy-to-use dashboard.' },
            ].map((item) => (
              <div key={item.step} className="relative rounded-2xl border border-gray-200 bg-gray-50 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:bg-white hover:shadow-lg">
                <span className="text-5xl font-bold text-orange-100">{item.step}</span>
                <h3 className="mt-8 text-xl font-bold text-gray-900">{item.title}</h3>
                <p className="mt-3 leading-relaxed text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-orange-500/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="container-max section-padding relative text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Go Digital Today!
          </h2>
          <p className="text-gray-300 text-xl mb-4 max-w-2xl mx-auto">
            We have a seamless, self sign-up process so that you can become a partner in a matter of minutes.
          </p>
          <p className="text-orange-400 font-semibold text-lg mb-10">
            We're proud to be the fastest solution in making your business go live.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-xl shadow-orange-500/30"
            >
              Join Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-gray-600 hover:border-orange-500 text-gray-300 hover:text-orange-400 px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              <Phone className="mr-2 h-5 w-5" /> Talk to Sales
            </Link>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-orange-500" /> No setup fees
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-orange-500" /> Go live in minutes
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-orange-500" /> 24/7 support
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-orange-500" /> support@mobipay.pk
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default HomePage
